#!/usr/bin/env node
// elp-moon.mjs — the Moon's geocentric position, truncated ELP2000-82.
//
// FOUNDER, 2026-08-02: "now do the moon and stars too."
//
// The Moon is the hardest body in the almanac and the most useful sight after the Sun: it is often the
// only body visible in daylight besides the Sun, which is what makes a Sun-Moon fix possible. Its
// motion is also far messier than the Sun's — the Earth-Moon system is pulled about by the Sun, and the
// series below is that mess, term by term. D is the elongation from the Sun, M the Sun's mean anomaly,
// M' the Moon's own, F the argument of latitude. Every row is a real periodic perturbation.
//
// SAME DISCIPLINE AS THE SUN: the accuracy claim is MEASURED against an independent implementation, not
// asserted from theory. The Sun's comment once claimed 0.01' and was wrong by fifty times.
const D2R = Math.PI / 180;
const sin = (d) => Math.sin(d * D2R), cos = (d) => Math.cos(d * D2R);
const norm360 = (a) => ((a % 360) + 360) % 360;
const poly = (T, ...c) => c.reduce((a, k, i) => a + k * T ** i, 0);

// Table 47.A — arguments (D, M, M', F) with longitude (1e-6 deg) and distance (1e-3 km) coefficients
const TA = [
[0,0,1,0,6288774,-20905355],[2,0,-1,0,1274027,-3699111],[2,0,0,0,658314,-2955968],[0,0,2,0,213618,-569925],
[0,1,0,0,-185116,48888],[0,0,0,2,-114332,-3149],[2,0,-2,0,58793,246158],[2,-1,-1,0,57066,-152138],
[2,0,1,0,53322,-170733],[2,-1,0,0,45758,-204586],[0,1,-1,0,-40923,-129620],[1,0,0,0,-34720,108743],
[0,1,1,0,-30383,104755],[2,0,0,-2,15327,10321],[0,0,1,2,-12528,0],[0,0,1,-2,10980,79661],
[4,0,-1,0,10675,-34782],[0,0,3,0,10034,-23210],[4,0,-2,0,8548,-21636],[2,1,-1,0,-7888,24208],
[2,1,0,0,-6766,30824],[1,0,-1,0,-5163,-8379],[1,1,0,0,4987,-16675],[2,-1,1,0,4036,-12831],
[2,0,2,0,3994,-10445],[4,0,0,0,3861,-11650],[2,0,-3,0,3665,14403],[0,1,-2,0,-2689,-7003],
[2,0,-1,2,-2602,0],[2,-1,-2,0,2390,10056],[1,0,1,0,-2348,6322],[2,-2,0,0,2236,-9884],
[0,1,2,0,-2120,5751],[0,2,0,0,-2069,0],[2,-2,-1,0,2048,-4950],[2,0,1,-2,-1773,4130],
[2,0,0,2,-1595,0],[4,-1,-1,0,1215,-3958],[0,0,2,2,-1110,0],[3,0,-1,0,-892,3258],
[2,1,1,0,-810,2616],[4,-1,-2,0,759,-1897],[0,2,-1,0,-713,-2117],[2,2,-1,0,-700,2354],
[2,1,-2,0,691,0],[2,-1,0,-2,596,0],[4,0,1,0,549,-1423],[0,0,4,0,537,-1117],
[4,-1,0,0,520,-1571],[1,0,-2,0,-487,-1739],[2,1,0,-2,-399,0],[0,0,2,-2,-381,-4421],
[1,1,1,0,351,0],[3,0,-2,0,-340,0],[4,0,-3,0,330,0],[2,-1,2,0,327,0],
[0,2,1,0,-323,1165],[1,1,-1,0,299,0],[2,0,3,0,294,0],[2,0,-1,-2,0,8752]];

// Table 47.B — latitude (1e-6 deg)
const TB = [
[0,0,0,1,5128122],[0,0,1,1,280602],[0,0,1,-1,277693],[2,0,0,-1,173237],[2,0,-1,1,55413],
[2,0,-1,-1,46271],[2,0,0,1,32573],[0,0,2,1,17198],[2,0,1,-1,9266],[0,0,2,-1,8822],
[2,-1,0,-1,8216],[2,0,-2,-1,4324],[2,0,1,1,4200],[2,1,0,-1,-3359],[2,-1,-1,1,2463],
[2,-1,0,1,2211],[2,-1,-1,-1,2065],[0,1,-1,-1,-1870],[4,0,-1,-1,1828],[0,1,0,1,-1794],
[0,0,0,3,-1749],[0,1,-1,1,-1565],[1,0,0,1,-1491],[0,1,1,1,-1475],[0,1,1,-1,-1410],
[0,1,0,-1,-1344],[1,0,0,-1,-1335],[0,0,3,1,1107],[4,0,0,-1,1021],[4,0,-1,1,833],
[0,0,1,-3,777],[4,0,-2,1,671],[2,0,0,-3,607],[2,0,2,-1,596],[2,-1,1,-1,491],
[2,0,-2,1,-451],[0,0,3,-1,439],[2,0,2,1,422],[2,0,-3,-1,421],[2,1,-1,1,-366],
[2,1,0,1,-351],[4,0,0,1,331],[2,-1,1,1,315],[2,-2,0,-1,302],[0,0,1,3,-283],
[2,1,1,-1,-229],[1,1,0,-1,223],[1,1,0,1,223],[0,1,-2,-1,-220],[2,1,-1,-1,-220],
[1,0,1,1,-185],[2,-1,-2,-1,181],[0,1,2,1,-177],[4,0,-2,-1,176],[4,-1,-1,-1,166],
[1,0,1,-1,-164],[4,0,1,-1,132],[1,0,-1,-1,-119],[4,-1,0,-1,115],[2,-2,0,1,107]];

/** Geocentric ecliptic longitude (deg), latitude (deg) and distance (km) of the Moon. */
export function moon(T) {
  const Lp = norm360(poly(T, 218.3164477, 481267.88123421, -0.0015786, 1 / 538841, -1 / 65194000));
  const D  = norm360(poly(T, 297.8501921, 445267.1114034, -0.0018819, 1 / 545868, -1 / 113065000));
  const M  = norm360(poly(T, 357.5291092, 35999.0502909, -0.0001536, 1 / 24490000));
  const Mp = norm360(poly(T, 134.9633964, 477198.8675055, 0.0087414, 1 / 69699, -1 / 14712000));
  const F  = norm360(poly(T, 93.2720950, 483202.0175233, -0.0036539, -1 / 3526000, 1 / 863310000));
  const A1 = norm360(119.75 + 131.849 * T);
  const A2 = norm360(53.09 + 479264.290 * T);
  const A3 = norm360(313.45 + 481266.484 * T);
  // terms involving the Sun's anomaly are scaled by E — the earth's orbital eccentricity changes slowly
  const E = 1 - 0.002516 * T - 0.0000074 * T * T;

  let sl = 0, sr = 0, sb = 0;
  for (const [d, m, mp, f, l, r] of TA) {
    const e = Math.abs(m) === 1 ? E : (Math.abs(m) === 2 ? E * E : 1);
    const arg = d * D + m * M + mp * Mp + f * F;
    sl += l * e * sin(arg); sr += r * e * cos(arg);
  }
  for (const [d, m, mp, f, b] of TB) {
    const e = Math.abs(m) === 1 ? E : (Math.abs(m) === 2 ? E * E : 1);
    sb += b * e * sin(d * D + m * M + mp * Mp + f * F);
  }
  // additive terms: Venus (A1), Jupiter (A2) and the flattening of the earth (A3)
  sl += 3958 * sin(A1) + 1962 * sin(Lp - F) + 318 * sin(A2);
  sb += -2235 * sin(Lp) + 382 * sin(A3) + 175 * sin(A1 - F) + 175 * sin(A1 + F)
      + 127 * sin(Lp - Mp) - 115 * sin(Lp + Mp);

  const lambda = norm360(Lp + sl / 1e6);
  const beta = sb / 1e6;
  const delta = 385000.56 + sr / 1000;                  // km
  return { lambda, beta, delta, parallax: Math.asin(6378.14 / delta) / D2R, Lp, D, M, Mp, F };
}
